import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, PenLine, Globe, Check, Monitor, MapPin, ThumbsUp, FileText, Clock, PoundSterling } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Btn } from "@/components/Btn";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ASSETS, ESSENTIAL_FEATURES, ADVANCED_ADDS, COMMERCE_ADDS, PROCESS, PROJECTS, FAQS } from "@/lib/data";

const ease = [0.16, 1, 0.3, 1];

/* HERO */
function Hero() {
  return (
    <section data-testid="hero" className="relative min-h-screen overflow-hidden bg-ink text-white">
      <div className="absolute inset-0">
        <img src={ASSETS.heroForm} alt="" aria-hidden="true" className="absolute right-0 top-0 h-full w-full object-cover opacity-[0.78] md:w-[58%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
      </div>
      <div className="relative mx-auto flex min-h-screen max-w-[1440px] flex-col justify-center px-6 pt-28 md:px-12">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
          className="text-[11px] uppercase tracking-label text-champagne">Poole · Dorset · UK</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2, ease }}
          className="mt-7 max-w-4xl font-display text-[14vw] font-medium leading-[0.94] tracking-tight sm:text-6xl lg:text-8xl">
          Websites that<br />move businesses<br /><span className="font-serif italic font-normal text-champagne">forward.</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-9 max-w-md text-lg leading-relaxed text-white/80">
          Premium websites and digital services for ambitious businesses across the UK.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.62 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
          <Btn to="/work" variant="gold" testid="hero-view-work">View our Work</Btn>
          <Btn to="/pricing" variant="outline" dark testid="hero-view-pricing">View Pricing</Btn>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.9 }}
          className="mt-16 grid max-w-2xl grid-cols-1 gap-8 border-t border-white/10 pt-8 sm:grid-cols-3">
          {[[ShieldCheck, "Clear pricing", "No hidden costs."], [PenLine, "Bespoke design", "Built around your business."], [Globe, "UK-wide service", "Local studio, nationwide."]].map(([Icon, t, s]) => (
            <div key={t} className="flex items-start gap-3">
              <Icon size={20} strokeWidth={1.4} className="mt-0.5 text-champagne" />
              <div><p className="text-[11px] uppercase tracking-label">{t}</p><p className="mt-1 text-sm text-white/60">{s}</p></div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* REFERRAL PROGRAMME */
function Referral() {
  const steps = [
    ["01", "Refer", "Recommend a business that needs a website."],
    ["WAVE200", "Code", "They use referral code WAVE200 and tell us who referred them."],
    ["£200", "Earn", "They become a website client. You get £200."],
  ];
  return (
    <section data-testid="referral-section" className="bg-ink text-white">
      <div className="mx-auto max-w-[1440px] px-6 py-16 md:px-12 md:py-20">
        <div className="grid gap-12 md:grid-cols-12 md:items-center">
          {/* Left */}
          <div className="md:col-span-5">
            <p className="text-[11px] uppercase tracking-label text-champagne">Wave Eleven Referral Programme</p>
            <h2 className="mt-5 font-display text-4xl font-semibold uppercase leading-[0.98] tracking-tight md:text-5xl">
              Refer a business.<br />Earn <span className="font-serif text-5xl font-normal normal-case italic text-champagne md:text-6xl">£200.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">
              Know a business that needs a new website? Recommend Wave Eleven. If they become a website client, we'll pay you £200.
            </p>
            <div className="mt-8"><Btn to="/referrals" variant="gold" testid="referral-cta">How referrals work</Btn></div>
          </div>
          {/* Right */}
          <div className="md:col-span-6 md:col-start-7">
            <div className="grid gap-8 sm:grid-cols-3">
              {steps.map(([mark, title, copy]) => (
                <div key={title} className="border-t border-white/15 pt-5">
                  <p className="font-serif text-4xl leading-none text-champagne md:text-5xl">{mark}</p>
                  <p className="mt-4 text-[11px] uppercase tracking-label text-white/90">{title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{copy}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-[11px] uppercase tracking-label text-white/50">No sign-up. No targets. No limits.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* SERVICES SNAPSHOT */
function Snapshot() {
  const cards = [
    { icon: Monitor, title: "Websites", copy: "Bespoke websites designed around your business.", price: "From £895", to: "/pricing" },
    { icon: MapPin, title: "Google Business", copy: "Get found by more local customers on Google.", price: "£175", to: "/services" },
    { icon: ThumbsUp, title: "Social Media", copy: "A professional launch for your Facebook & Instagram.", price: "£175", to: "/services" },
  ];
  return (
    <section className="bg-paper-cream text-ink">
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32">
        <Reveal><h2 className="mx-auto max-w-3xl text-center font-serif text-3xl leading-tight tracking-tight md:text-5xl">Everything you need to move your business forward.</h2></Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <Link to={c.to} data-testid={`snapshot-${c.title.toLowerCase().replace(/ /g, "-")}`}
                className="group flex h-full flex-col border border-ink/10 bg-paper p-8 transition-colors duration-500 hover:border-champagne">
                <c.icon size={26} strokeWidth={1.3} className="text-champagne" />
                <h3 className="mt-8 text-[11px] uppercase tracking-label">{c.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/60">{c.copy}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-label text-ink">{c.price}<span className="transition-transform duration-500 group-hover:translate-x-1">→</span></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* PACKAGES */
function Packages() {
  return (
    <section id="packages" className="bg-paper text-ink">
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32">
        <Reveal>
          <p className="text-[11px] uppercase tracking-label text-ink/45">Website Packages</p>
          <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-[1.05] tracking-tight md:text-5xl">Clear pricing. <span className="italic text-champagne">No hidden costs.</span></h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">Choose the level of functionality your business needs.</p>
        </Reveal>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {/* Essential */}
          <Reveal>
            <div data-testid="package-essential" className="flex h-full flex-col border border-ink/12 bg-paper-cream p-8">
              <p className="text-[11px] uppercase tracking-label text-ink/60">Essential</p>
              <p className="mt-6 text-[10px] uppercase tracking-label text-ink/40">One-off build price</p>
              <p className="font-serif text-6xl">£895</p>
              <p className="mt-4 text-sm leading-relaxed text-ink/65">Everything you need for a beautiful, professional online presence.</p>
              <ul className="mt-6 space-y-2.5">
                {ESSENTIAL_FEATURES.map((f) => (<li key={f} className="flex items-center gap-3 text-sm text-ink/75"><Check size={15} className="text-champagne" strokeWidth={2} />{f}</li>))}
              </ul>
              <div className="mt-auto pt-8"><Btn to="/start?package=website_essential" variant="primary" testid="essential-cta" className="w-full">Start my Website</Btn></div>
            </div>
          </Reveal>
          {/* Advanced */}
          <Reveal delay={0.06}>
            <div data-testid="package-advanced" className="flex h-full flex-col bg-ink p-8 text-white">
              <p className="text-[11px] uppercase tracking-label text-champagne">Advanced</p>
              <p className="mt-6 text-[10px] uppercase tracking-label text-white/40">One-off build price</p>
              <p className="font-serif text-6xl">£1,395</p>
              <p className="mt-4 text-sm text-white/70">Everything in Essential, plus:</p>
              <ul className="mt-6 space-y-2.5">
                {ADVANCED_ADDS.map((f) => (<li key={f} className="flex items-center gap-3 text-sm text-white/85"><Check size={15} className="text-champagne" strokeWidth={2} />{f}</li>))}
              </ul>
              <div className="mt-auto pt-8"><Btn to="/start?package=website_advanced" variant="gold" testid="advanced-cta" className="w-full">Start my Website</Btn></div>
            </div>
          </Reveal>
          {/* Commerce */}
          <Reveal delay={0.12}>
            <div data-testid="package-commerce" className="flex h-full flex-col border border-ink/12 bg-paper-cream p-8">
              <p className="text-[11px] uppercase tracking-label text-ink/60">Commerce</p>
              <p className="mt-6 text-[10px] uppercase tracking-label text-ink/40">One-off build price</p>
              <p className="font-serif text-6xl">£1,995</p>
              <p className="mt-4 text-sm leading-relaxed text-ink/65">A complete ecommerce website for businesses ready to sell online.</p>
              <ul className="mt-6 space-y-2.5">
                {COMMERCE_ADDS.map((f) => (<li key={f} className="flex items-center gap-3 text-sm text-ink/75"><Check size={15} className="text-champagne" strokeWidth={2} />{f}</li>))}
              </ul>
              <div className="mt-auto pt-8"><Btn to="/start?package=website_commerce" variant="primary" testid="commerce-cta" className="w-full">Start my Website</Btn></div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="mt-6 flex flex-col items-start justify-between gap-4 border border-ink/12 bg-paper-cream px-8 py-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <ShieldCheck size={22} strokeWidth={1.4} className="text-champagne" />
              <div>
                <p className="text-[11px] uppercase tracking-label">Aftercare — £29 / month</p>
                <p className="mt-1 text-sm text-ink/60">First 3 months included · Month-to-month · No long-term contract</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* PROPOSITION */
function Proposition() {
  return (
    <section className="bg-ink text-white">
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32">
        <div className="grid gap-12 md:grid-cols-12 md:items-center">
          <div className="md:col-span-4">
            <Reveal><h2 className="font-serif text-4xl leading-[1.05] tracking-tight md:text-5xl">Buying a website<br />shouldn't be<br /><span className="italic text-champagne">complicated.</span></h2></Reveal>
          </div>
          <div className="md:col-span-8">
            <div className="grid gap-8 sm:grid-cols-3">
              {[[FileText, "No quotes", "You see the price before you start."], [Clock, "No waiting around", "Start your project today, not next month."], [PoundSterling, "No hidden costs", "What we say is what you pay."]].map(([Icon, t, s], i) => (
                <Reveal key={t} delay={i * 0.06}>
                  <div className="border-l border-white/15 pl-5"><Icon size={22} strokeWidth={1.4} className="text-champagne" /><p className="mt-5 text-[11px] uppercase tracking-label">{t}</p><p className="mt-2 text-sm leading-relaxed text-white/60">{s}</p></div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <div className="mt-12 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
                <Btn to="/start" variant="gold" testid="prop-cta">Choose your Website</Btn>
                <p className="max-w-sm text-sm text-white/60">Choose your package, tell us about your business and we'll take it from there.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* SELECTED WORK */
function SelectedWork() {
  return (
    <section className="bg-paper-cream text-ink">
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32">
        <div className="flex items-end justify-between">
          <Reveal><h2 className="font-serif text-3xl tracking-tight md:text-5xl">Selected work.</h2></Reveal>
          <Link to="/work" className="hidden items-center gap-2 text-[11px] uppercase tracking-label text-ink transition-opacity hover:opacity-60 sm:inline-flex">View all projects →</Link>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.06}>
              <Link to={`/work/${p.slug}`} data-testid={`work-${p.slug}`} className="group relative block aspect-[3/4] overflow-hidden">
                <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <p className="text-[10px] uppercase tracking-label text-champagne">{p.type}</p>
                  <h3 className="mt-1 font-serif text-xl">{p.title}</h3>
                  <p className="mt-1 text-[10px] uppercase tracking-label text-white/70">{p.tags.join(" · ")}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* PROCESS */
function ProcessRow() {
  return (
    <section className="bg-paper text-ink">
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32">
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-label text-ink/45">How it works</p>
          <h2 className="mt-4 font-serif text-3xl tracking-tight md:text-5xl">From idea to live website.</h2>
        </div>
        <div className="mt-16 grid gap-10 md:grid-cols-5">
          {PROCESS.map((p, i) => (
            <Reveal key={p.no} delay={i * 0.05}>
              <div className="text-center md:text-left">
                <span className="font-serif text-2xl text-champagne">{p.no}</span>
                <h3 className="mt-3 text-[11px] uppercase tracking-label">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}><div className="mt-14 text-center"><Btn to="/start" variant="primary" testid="process-cta">Start my Project</Btn></div></Reveal>
      </div>
    </section>
  );
}

/* ADDITIONAL SERVICES */
function Additional() {
  const cards = [
    { icon: MapPin, title: "Google Business Launch & Local SEO", price: "£175", note: "One-off payment", copy: "Set up and optimise your Google Business Profile to get found on Search and Maps.", pkg: "google_business" },
    { icon: ThumbsUp, title: "Business Social Media Launch", price: "£175", note: "One-off payment", copy: "Professional Facebook & Instagram setup, branded and ready to make an impact.", pkg: "social_media" },
  ];
  return (
    <section className="bg-paper-cream text-ink">
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Reveal><p className="text-[11px] uppercase tracking-label text-ink/45">Additional Services</p>
            <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight md:text-5xl">Power your online presence.</h2></Reveal>
          </div>
          <div className="grid gap-6 md:col-span-8 md:grid-cols-2">
            {cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="flex h-full flex-col border border-ink/12 bg-paper p-8">
                  <c.icon size={24} strokeWidth={1.3} className="text-champagne" />
                  <h3 className="mt-6 text-[11px] uppercase tracking-label leading-relaxed">{c.title}</h3>
                  <p className="mt-4 font-serif text-4xl">{c.price}</p>
                  <p className="text-[10px] uppercase tracking-label text-ink/40">{c.note}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-ink/60">{c.copy}</p>
                  <div className="mt-8"><Btn to={`/start?package=${c.pkg}`} variant="outline" dark testid={`additional-${c.pkg}`}>Get started</Btn></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* FAQ */
function Faq() {
  return (
    <section className="bg-paper text-ink">
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32">
        <Reveal><h2 className="font-serif text-3xl tracking-tight md:text-5xl">Answers to common questions.</h2></Reveal>
        <div className="mt-12">
          <Accordion type="single" collapsible className="grid gap-x-16 md:grid-cols-2">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`i${i}`} data-testid={`faq-${i}`} className="border-b border-ink/12">
                <AccordionTrigger className="py-5 text-left text-sm font-medium text-ink hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-ink/65">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* FINAL CTA */
function FinalCta() {
  return (
    <section data-testid="final-cta" className="bg-champagne text-ink">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-6 px-6 py-10 md:flex-row md:px-12">
        <p className="font-serif text-2xl tracking-tight md:text-3xl">Ready to move your business forward?</p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <span className="text-[11px] uppercase tracking-label text-ink/70">Essential £895 · Advanced £1,395</span>
          <Btn to="/start" variant="primary" testid="final-start">Start a Project</Btn>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Referral />
      <Snapshot />
      <Packages />
      <Proposition />
      <SelectedWork />
      <ProcessRow />
      <Additional />
      <Faq />
      <FinalCta />
    </>
  );
}
